/* tslint:disable:no-trailing-whitespace import-spacing no-inferrable-types typedef-whitespace */

import {
  ActivatedRoute,
  ParamMap,
  Router}                        from '@angular/router';
import { first }                 from 'rxjs/operators';
import { Injectable }            from '@angular/core';
import {
  HttpClient,
  HttpHeaders
}                               from '@angular/common/http';
import {
  Observable,
  Subject
}                               from 'rxjs';

import { AuthenticationService} from '@/services/authentication.service';
import { User }                 from '@/models/user';
import { Logger }               from '@/util/logger';
import { Subdivision }          from '@/models/subdivision';
import { errorAsText }          from '@/util/util';
import { PanoramaSelection }    from '@/models/panorama.selection';
import { Panorama }             from '@/models/panorama';

import * as $                   from 'jquery';

@Injectable({
  providedIn: 'root'
})

/**
 *
 * @class PanoramaService
 *
 * @brief access to panoramic data / locations
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      July 2022
 *
 */
export class PanoramaService {

  /** the corridor line string cache */
  private static _routeCache: any                     = null;

  /** the panorama point data cache */
  private static _dataCache: any                      = null;

  /** the cache of individual panoramas */
  private static _panoCache: Panorama[]               = [];

  /** the subdivision/timelines */
  private static _subDivisions: Subdivision[]         = null;

  /** the panorama in focus */
  private static _panorama: Panorama                  = null;

  /** the selected subdivision with timelinle/milepost hinting */
  private static _selected: PanoramaSelection         = null;

  /** publish updates when we get a new set of subdivisions */
  private subSubject   = new Subject<Subdivision[]>();

  /** publish updates when we get a new panorama focus */
  private panoSubject  = new Subject<Panorama>();

  /** standard constructor */
  constructor(
    private   http: HttpClient,
    private   auth: AuthenticationService,
    private router: Router,
    private  route: ActivatedRoute
  ) {

    const ps: PanoramaService = this;

    this.auth.observableUser.subscribe(

    (newUser) => {

        Logger.debug(`[PanogramaService] user switched, clearing cache...`);

        ps.clearCache();

        Logger.debug(`[PanogramaService] cache cleared.`);

      });
  }

  /** allow for subscribing to change in the subdivision set */
  onSubvidivisions(): Observable<Subdivision[]> {

    return this.subSubject.asObservable();
  }

  /** allow for subscribing to panorama focus change */
  onPanorama(): Observable<Panorama> {

    return this.panoSubject.asObservable();
  }

  /** fetch any headers we need to send along with our request */
  headers() : HttpHeaders {

    const user: User = this.auth.userOfSession;

    if (!user) {

      /* no user logged in */

      return null;
    }

    return new HttpHeaders().set('Authorization', user.token).set('Content-Type', 'application/json');
  }

  /**
   *
   * navigate (back) to the overwatch screen and center on (x,y) with zoom level z
   *
   * @param x the lat to center on
   * @param y the lng to center on
   * @param z the zoom level to set
   *
   */
  overwatchNavigate(x: number, y: number, z: number) {

    const uri: string    = `/overwatch?rx=${x}&ry=${y}&rz=${z}`;

    Logger.debug(`[PanoramaService] backAction - return to x=${x}, y=${y}, zoom=${z}`);
    Logger.debug(`[PanoramaService] backAction - jump to: ${uri} ...`);

    this.router.navigateByUrl(uri);
  }

  /**
   *
   * navigate to the panorama viewer screen, and provider the (x,y) center and z zoom level of where
   * to overwatch watch was pan/zoomed to...so the panoramma viewer knows what to return to (i.e. the
   * back button).
   *
   * @note if you don't provide the return settings for jumping back to the overview screen,
   * they will be inferred from current URL.
   *
   * @param id the specific panorama to view (when returning to overwatch)
   * @param rx the overwatch x to center on (when returning to overwatch)
   * @param ry the overwatch y to center on (when returning to overwatch)
   * @param rz the overwatch z to set (when returning to overwatch)
   *
   */
  panoramaNavigate(id: number, rx: number=0, ry: number=0, rz: number=0) {

    // tslint:disable-next-line
    const params: ParamMap    = this.route.snapshot['queryParamMap'];

    if (rx === 0) {

      if (params.has('rx')) {

        rx = Number(params.get('rx'));
      }
    }

    if (ry === 0) {

      if (params.has('ry')) {

        ry = Number(params.get('ry'));
      }
    }

    if (rz === 0) {

      if (params.has('rz')) {

        rz = Number(params.get('rz'));
      }
    }

    /* build out the URI to use... */

    let uri: string    = `/panorama?id=${id}`;

    if (rx && ry) {

      uri += `&rx=${rx}&ry=${ry}`;
    }

    if (rz) {

      uri += `&rz=${rz}`;
    }

    Logger.debug(`[PanoramaService] panoramaNavigate - return to x=${rx}, y=${ry}, zoom=${rz}`);
    Logger.debug(`[PanoramaService] panoramaNavigate - jump to: ${uri} ...`);

    this.router.navigateByUrl(uri);
  }

  /** just fetch the corridor info */
  corridors() {

    const headers = this.headers();

    if (!headers) {

      return null;
    }

    return this.http.get(`/backend/corridors`,  {
      headers
    });
  }

  /** all-in-one fetch of info needed on the splash page */
  overwatch(subdivisionOnly: boolean) {

    const headers = this.headers();

    if (!headers) {

      return null;
    }

    let url = `/backend/overwatch`;

    if (subdivisionOnly) {

      url += `?subsOnly=true`;
    }

    return this.http.get(url,  {
      headers
    });
  }

  /** get the (maybe cached not) routes */
  routes(): any {

    return PanoramaService._routeCache;
  }

  /** get the (maybe cached not) panorama points */
  data(): any {

    return PanoramaService._dataCache;
  }

  /** note which subdivision and timeline is shown in the milepost navigator */
  select(sel: PanoramaSelection = null) {

    PanoramaService._selected = sel;
  }

  /** fetch the last known selected subdivision and timeline */
  selection(): PanoramaSelection {

    return PanoramaService._selected;
  }

  /** set the current panorama in focus */
  setPanorama(p: Panorama) {

    const ps: PanoramaService = this;

    PanoramaService._panorama = p;

    ps.panoSubject.next(PanoramaService._panorama);
  }

  /** fetch the current panorama */
  current(): Panorama|null {

    return PanoramaService._panorama;
  }

  /** tell any listeners there is fresh baked data */
  refresh(subdivisionOnly: boolean): boolean {

    const ps: PanoramaService = this;

    if (PanoramaService._subDivisions) {

      Logger.debug(`[PanoramaService] publishing cached subdivisions.`);

      /* publish the subdivisions */

      ps.subSubject.next(PanoramaService._subDivisions);

      return true;
    }

    /* we don't have cached data yet, go and get it... */

    Logger.debug(`[PanoramaService] fetching overwatch data...`);

    ps.overwatch(subdivisionOnly)
      .pipe(first())
      .subscribe(
        (data: any) => {

          ps.cacheRoutes(data.result.routes);

          /* in minimap mode we don't have the global set of panorama points */

          if (data.result.hasOwnProperty('panoramas')) {

            ps.cachePanoramaPoints(data.result.panoramas);
          }

          const subs: Subdivision[] = [];

          for (const [cName, sObj] of Object.entries(data.result.tree)) {

            for (const [sName, tList] of Object.entries(sObj)) {

              let idx: number = subs.findIndex(x => x.name === sName);

              /* if we haven't seen it yet, add it */

              if (idx < 0) {

                subs.push(new Subdivision({name: sName, timelines: tList}));

                continue;
              }

              idx = subs.findIndex(x => x.name === sName);

              const sub: Subdivision = subs[idx];

              /* if we already have it, merge the timelines */

              for (const item of tList) {

                sub.addYearMonth(item);
              }

            }
          }

          ps.cacheSubdivisions(subs);

          Logger.debug(`[PanoramaService] publishing (via overwatch) cached subdivisions.`);

          /* publish the subdivisions */

          ps.subSubject.next(PanoramaService._subDivisions);

        },
        (error) => {

          /* something went horribly, horribly wrong! */

          Logger.error(`[PanoramaService] can't cache overwatch data: `, errorAsText(error));
        });

  }

  /** cache the subdivisions/timelines */
  cacheSubdivisions(subs: Subdivision[]): boolean {

    PanoramaService._subDivisions = subs;

    return true;
  }

  /** cache the panorama points (10's of thousands), available to this user */
  cachePanoramaPoints(data: any): boolean {

    PanoramaService._dataCache = data;

    return true;
  }

  /** cache the corridor line string geometries available to this user */
  cacheRoutes(routes: any ): boolean {

    PanoramaService._routeCache = routes;

    return true;
  }

  /** when users login, we need to ensure they are getting fresh data. */
  clearCache(): boolean {

    PanoramaService._dataCache  = null;
    PanoramaService._routeCache = null;

    PanoramaService._panoCache  = [];

    return true;
  }

  /** locate panorama nearest this mile post */
  mile(mile: number, sub: string, timeline: string) {

    const headers = this.headers();

    if (!headers) {

      return null;
    }

    return this.http.get(`/backend/nearest/mile/${mile}/${sub}/${timeline}`,  {
      headers
    });
  }

  /** fetch all the panoramas in the given bounding box */
  bounded(t: number, r: number, b: number, l: number, sub: string='', timeline: string='')  {

    const headers = this.headers();

    if (!headers) {

      return null;
    }

    let url: string = `/backend/bounded/${t}/${r}/${b}/${l}`;

    if (sub && timeline) {

      url += `?sub=${sub}&timeline=${timeline}`;
    }

    return this.http.get(url,  {
      headers
    });
  }

  /** locate panorama nearest this lat / lng pair */
  nearest(x: number, y:number, sub: string='', timeline: string='') {

    /*
     * normally this is used in a couple of ways:
     *
     *   - convert raw lat/lng to panorama
     *
     *   - convert from the lat/lng of a current panorama to whatever is closest...and in the given timeline.
     *     this allows us to gracefully jump between timelines.
     *
     */

    const headers = this.headers();

    if (!headers) {

      return null;
    }

    let url: string = `/backend/nearest/${x}/${y}`;

    if (sub && timeline) {

      url += `?sub=${sub}&timeline=${timeline}`;
    }

    return this.http.get(url,  {
      headers
    });
  }

  /** fetch the metadata for this specific panorama, but key by its UUID */
  panoramaOf(uuid: string) {

    const headers = this.headers();

    if (!headers) {

      return null;
    }

    return this.http.get(`/backend/panorama/uuid/${uuid}`,  {
      headers
    });
  }

  /** test if we have a panorama cached */
  isPanoramaCached(id: number): boolean {

    return !!PanoramaService._panoCache.find(x => x.id === id);
  }

  /** fetch the cached panorama (if there is one */
  getPanorama(id: number): Panorama {

    const pano: Panorama = PanoramaService._panoCache.find(x => x.id === id);

    if (!pano) {

      /* not in the cache */
      return null;
    }

    return pano;
  }

  /** cache a panorama */
  cachePanorama(pano: Panorama) {

    PanoramaService._panoCache.push(pano);

    /* don't let the cache get too big */

    if (PanoramaService._panoCache.length > 100) {

      PanoramaService._panoCache.shift();
    }

  }

  /** fetch metadata for this specific panorama */
  panorama(id: number) {

    const headers = this.headers();

    if (!headers) {

      return null;
    }

    return this.http.get(`/backend/panorama/${id}`,  {
      headers
    });
  }

  /** trigger a database sync */
  sync() {

    const headers = this.headers();

    if (!headers) {

      return null;
    }

    return this.http.put(`/backend/sync/launch`,  {
      headers
    });
  }

  /** fetch the most recent jobs */
  recentJobs(limit: number = 5) {

    const headers = this.headers();

    if (!headers) {

      return null;
    }

    return this.http.get(`/backend/sync/jobs?limit=${limit}`,  {
      headers
    });
  }

}
